import { useQuery } from "@tanstack/react-query";
import type { Metric, MetricsResponse } from "@unbox/api";
import { handleError } from "@/api/error";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

// Sort time period data chronologically by date
const sortTimePeriodDataByDate = <T extends { periodStart?: string }>(data: T[]): T[] => {
  if (!data || data.length === 0) return data;

  return [...data].sort((a, b) => {
    const dateA = new Date(a.periodStart || "");
    const dateB = new Date(b.periodStart || "");
    return dateA.getTime() - dateB.getTime();
  });
};

// Sort all time series data in the Overview data
const sortMetrics = (data: NonNullable<MetricsResponse>): NonNullable<MetricsResponse> => {
  return Object.fromEntries(
    Object.entries(data).map(([key, val]) => [key, sortTimePeriodDataByDate(val as { periodStart: string }[])]),
  ) as NonNullable<MetricsResponse>;
};

export const useMetrics = (
  metrics: Metric[],
  timePeriod: "daily" | "weekly" | "monthly" = "weekly",
  // When both are provided the server scopes the data to the range and returns
  // all-source GMV; otherwise it falls back to the trailing-window default.
  from?: string,
  to?: string,
) => {
  const scope = useScopedMerchantClient();
  const range = from && to ? { from, to } : {};
  return useQuery({
    queryKey: ["metrics", scope.merchantId, scope.workspaceId, timePeriod, from, to, metrics],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].metrics.$get({
            param: scope.param,
            query: {
              timePeriod,
              metrics,
              ...range,
            },
          })
        : await client.merchant[":merchantId"].metrics.$get({
            param: scope.param,
            query: {
              timePeriod,
              metrics,
              ...range,
            },
          });
      if (!res.ok) throw await handleError(res);
      const data = await res.json();
      if (data === null) return null;
      return sortMetrics(data);
    },
    enabled: !!scope.merchantId,
  });
};
